import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { map, mergeMap, switchMap, catchError, filter, takeUntil } from 'rxjs/operators';
import { ProjectsService } from '@core/services/projects.service';
import * as ProjectActions from './projects.actions';

@Injectable()
export class ProjectEffects {
    private actions$ = inject(Actions);
    private translate = inject(TranslateService);
    private ProjectsService = inject(ProjectsService);

    loadProjects$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.loadProjects),
            mergeMap(() =>
                this.ProjectsService.loadProjects().pipe(
                    map((projects) => ProjectActions.loadProjectsSuccess({ projects })),
                    catchError((error) =>
                        of(
                            ProjectActions.loadProjectsFailure({
                                error: error.message || this.translate.instant('storeErrors.loadProjects'),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    createProjectSuccessReload$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.createProjectSuccess),
            map(() => ProjectActions.loadProjects()),
        ),
    );

    createProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.createProject),
            mergeMap(({ projectData }) =>
                this.ProjectsService.createProject(projectData).pipe(
                    map((projectId) => ProjectActions.createProjectSuccess({ projectId })),
                    catchError((error) =>
                        of(
                            ProjectActions.createProjectFailure({
                                error: error.message || this.translate.instant('storeErrors.createProject'),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    updateProjectSuccessReload$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.updateProjectSuccess),
            map(() => ProjectActions.loadProjects()),
        ),
    );

    updateProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.updateProject),
            mergeMap(({ projectId, projectData }) =>
                this.ProjectsService.updateProject(projectId, projectData).pipe(
                    map((response) =>
                        ProjectActions.updateProjectSuccess({
                            projectId,
                            message: response.message,
                        }),
                    ),
                    catchError((error) =>
                        of(
                            ProjectActions.updateProjectFailure({
                                error: error.message || this.translate.instant('storeErrors.updateProject'),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    loadCampaignsOnProjectSelect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.setCurrentProject),
            filter(({ project }) => project !== null && project.campaign_ids?.length > 0),
            mergeMap(({ project }) => [
                ProjectActions.clearProjectCampaigns(),
                ProjectActions.loadProjectCampaigns({
                    campaignIds: project!.campaign_ids,
                }),
            ]),
        ),
    );

    loadProjectCampaigns$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.loadProjectCampaigns),
            switchMap(({ campaignIds }) =>
                this.ProjectsService.loadProjectCampaigns(campaignIds).pipe(
                    takeUntil(this.actions$.pipe(ofType(ProjectActions.clearProjectCampaigns))),
                    map((campaigns) =>
                        ProjectActions.loadProjectCampaignsSuccess({
                            campaigns,
                        }),
                    ),
                    catchError((error) =>
                        of(
                            ProjectActions.loadProjectCampaignsFailure({
                                error: error.message || this.translate.instant('storeErrors.loadCampaigns'),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.deleteProject),
            mergeMap(({ projectId }) =>
                this.ProjectsService.deleteProject(projectId).pipe(
                    map(() => ProjectActions.deleteProjectSuccess({ projectId })),
                    catchError((error) =>
                        of(
                            ProjectActions.deleteProjectFailure({
                                error: error.message || this.translate.instant('storeErrors.deleteProject'),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
}
